import { useState, useRef, useEffect } from "react";

/**
 * useReadyTour — opens a tour only once its surface is ready (settled).
 *
 * A tour that spotlights elements inside an animating/loading surface (e.g. a
 * drawer) must not open until the layout is stable, or it measures targets at
 * their pre-settle positions. Pass `active` (should it run) and `ready` (is the
 * surface settled, e.g. `!loading`); it opens once per activation, `delay`ms
 * after ready, and resets when `active` goes false.
 *
 *   const { open, remountKey } = useReadyTour({ active, ready: !loading });
 *   <SbTour key={remountKey} open={open} getPopupContainer={...} ... />
 *
 * `remountKey` bumps on each start so the tour remounts fresh (avoids AntD's
 * reopen/close-animation freeze).
 */
export default function useReadyTour({ active, ready, delay = 350 }) {
  const [open, setOpen] = useState(false);
  const [remountKey, setRemountKey] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!active) {
      startedRef.current = false;
      setOpen(false);
      return undefined;
    }
    if (startedRef.current || !ready) return undefined;
    const timer = setTimeout(() => {
      startedRef.current = true;
      setRemountKey((key) => key + 1);
      setOpen(true);
    }, delay);
    return () => clearTimeout(timer);
  }, [active, ready, delay]);

  return { open, remountKey };
}
